import type { AuditChange, AuditEntry } from '@/api/generated';

import { actionPhrase } from './actions';
import { EMPTY, fieldLabel, formatValue, formatValueText } from './formatValue';
import { targetTypeLabel } from './words';

/**
 * One entry as one sentence: what happened, to whom, and - when it fits - the
 * value before and after.
 *
 * The table's What happened column and the account page's panel both read
 * this, so an entry is worded the same wherever it appears.
 *
 * `changes` is what this viewer may see, already filtered. The sentence is
 * built from nothing else, so the organization's own views cannot mention a
 * staff-only field even by name. With nothing visible to describe, the
 * sentence is the action phrase the drawer's heading uses.
 *
 * The organization is never named: on the staff page it has its own column,
 * and on the organization's own page it is always the organization reading.
 * A person is named, because the row has nowhere else to say who.
 */

/** Longest a before or after value may be and still go in the sentence. */
const SHORT = 40;

/** A value as the sentence would show it, or null when it is too long to. */
function short(value: unknown, field: string): string | null {
  const formatted = formatValue(value, field);
  if (formatted.kind !== 'text' || formatted.text.length > SHORT) return null;
  return formatted.text;
}

/** "before → after", or null when either side is too long. */
function values(change: AuditChange): string | null {
  const before = short(change.before, change.field);
  const after = short(change.after, change.field);
  if (before === null || after === null) return null;
  return `${before} → ${after}`;
}

/** "Field: before → after", or null when either side is too long. */
function beforeAfter(change: AuditChange): string | null {
  const both = values(change);
  return both === null ? null : `${fieldLabel(change.field)}: ${both}`;
}

/** "A", "A and B", "A, B and C", or "A, B and 3 more". */
function fieldList(changes: AuditChange[]): string {
  const names = changes.map((change) => fieldLabel(change.field));
  if (names.length <= 3) {
    return names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/** One short change in full, or the fields by name after `lead`. */
function fieldsSentence(changes: AuditChange[], lead: string): string | null {
  if (changes.length === 0) return null;
  if (changes.length === 1) {
    const single = beforeAfter(changes[0]);
    if (single) return single;
  }
  return `${lead} ${fieldList(changes)}`;
}

function find(changes: AuditChange[], field: string): AuditChange | undefined {
  return changes.find((change) => change.field === field);
}

/** The role, in words, from the side of the `role` change asked for. */
function role(changes: AuditChange[], side: 'before' | 'after'): string | null {
  const change = find(changes, 'role');
  const value = change?.[side];
  if (value === null || value === undefined || value === '') return null;
  return formatValueText(value, 'role');
}

/** The person an entry is about, when it is about a person. */
function person(entry: AuditEntry): string | null {
  if (entry.target.type !== 'portal_user') return null;
  return entry.target.label || null;
}

/** "Added the logo", "Removed the cover image", from presence before and after. */
function imageSentence(changes: AuditChange[], noun: string): string | null {
  const change = changes[0];
  if (!change) return null;
  const had = change.before !== null && change.before !== undefined;
  const has = change.after !== null && change.after !== undefined;
  if (!had && has) return `Added the ${noun}`;
  if (had && !has) return `Removed the ${noun}`;
  if (had && has) return `Replaced the ${noun}`;
  return null;
}

function billingContactSentence(changes: AuditChange[]): string | null {
  const change = find(changes, 'billing_contact');
  if (!change) return null;
  const after = change.after;
  const name =
    after && typeof after === 'object' && 'name' in after
      ? (after as { name?: unknown }).name
      : null;
  if (typeof name === 'string' && name.trim()) {
    return `Changed the billing contact to ${name.trim()}`;
  }
  if (formatValueText(after, change.field) === EMPTY)
    return 'Removed the billing contact';
  return null;
}

/** A name in curly quotes, or null when the entry did not carry one. */
function quoted(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? `“${value.trim()}”` : null;
}

/**
 * The section or resource an ARC entry is about. The backend records it as a
 * change from nothing to the name (or, on a delete or removal, from the name
 * to nothing), so whichever side holds it is the name.
 */
function arcName(changes: AuditChange[], field: string): string | null {
  const change = find(changes, field);
  if (!change) return null;
  return quoted(change.after) ?? quoted(change.before);
}

/** The ARC's title and description, worded as the ARC's, not a user's job title. */
function arcDetailsSentence(changes: AuditChange[]): string | null {
  const title = find(changes, 'title');
  const description = find(changes, 'description');
  if (title && description) return 'Updated the ARC’s name and description';
  if (title) {
    const both = values(title);
    return both ? `Renamed the ARC: ${both}` : 'Renamed the ARC';
  }
  if (description) return 'Updated the ARC’s description';
  return null;
}

/** "Turned the Donate button on", and so on, for one header change. */
function arcHeaderChange(change: AuditChange): string | null {
  switch (change.field) {
    case 'donate_enabled':
      return `Turned the Donate button ${change.after ? 'on' : 'off'}`;
    case 'volunteer_enabled':
      return `Turned the Volunteer button ${change.after ? 'on' : 'off'}`;
    case 'intro_content':
      return 'Updated the introductory content';
    case 'logo_url':
      return imageSentence([change], 'ARC logo');
    case 'cover_image_url':
      return imageSentence([change], 'ARC cover image');
    default:
      return null;
  }
}

/** One header change in words; several at once fall back to the phrase. */
function arcHeaderSentence(changes: AuditChange[]): string | null {
  return changes.length === 1 ? arcHeaderChange(changes[0]) : null;
}

/** A rename, a hide or a show, or a rename and one of the other two. */
function arcSectionSentence(changes: AuditChange[]): string | null {
  const rename = find(changes, 'name');
  const visible = find(changes, 'visible');
  const shownOrHidden = visible ? (visible.after ? 'showed' : 'hid') : null;
  if (rename) {
    const from = quoted(rename.before);
    const to = quoted(rename.after);
    if (!from || !to) return null;
    const renamed = `Renamed the section ${from} to ${to}`;
    return shownOrHidden ? `${renamed} and ${shownOrHidden} it` : renamed;
  }
  const section = arcName(changes, 'section');
  if (!section || !shownOrHidden) return null;
  return `${shownOrHidden === 'hid' ? 'Hid' : 'Showed'} the section ${section}`;
}

/** A move between sections, a move within one, or a whole reorder of one. */
function arcItemsSentence(changes: AuditChange[]): string | null {
  const resource = arcName(changes, 'resource');
  const section = find(changes, 'section');
  if (find(changes, 'position') && resource) {
    const from = quoted(section?.before);
    const to = quoted(section?.after);
    if (from && to) return `Moved ${resource} from ${from} to ${to}`;
    const within = arcName(changes, 'section');
    return within ? `Moved ${resource} within ${within}` : `Moved ${resource}`;
  }
  const within = arcName(changes, 'section');
  return within ? `Reordered the content in ${within}` : null;
}

/** What a saved draft changed, in the words a person uses for each part. */
const DRAFT_PARTS: Record<string, string> = {
  title: 'name',
  description: 'description',
  intro_content: 'introductory text',
  donate_enabled: 'Donate button',
  volunteer_enabled: 'Volunteer button',
  logo_url: 'logo',
  cover_image_url: 'cover image',
  sections: 'sections',
  hidden: 'hidden sections',
  content: 'content',
};

/** "Saved the ARC draft: name, sections and content". */
function arcDraftSentence(changes: AuditChange[]): string | null {
  const parts = changes.map((change) => DRAFT_PARTS[change.field]).filter(Boolean);
  if (parts.length === 0) return null;
  const listed =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `Saved the ARC draft: ${listed}`;
}

/** " as Admin", or nothing. */
const as = (value: string | null) => (value ? ` as ${value}` : '');
/** " (Admin)", or nothing. */
const inBrackets = (value: string | null) => (value ? ` (${value})` : '');

function sentence(entry: AuditEntry, changes: AuditChange[]): string | null {
  const who = person(entry);

  switch (entry.action) {
    case 'organization.created':
      return 'Created the organization';
    case 'organization.updated':
      return fieldsSentence(changes, 'Updated');
    case 'organization.status_changed': {
      const status = find(changes, 'status');
      return status ? beforeAfter(status) : null;
    }
    case 'organization.profile_updated':
      return fieldsSentence(changes, 'Updated the profile:');
    case 'organization.logo_updated':
      return imageSentence(changes, 'logo');
    case 'organization.cover_updated':
      return imageSentence(changes, 'cover image');
    case 'organization.billing_contact_updated':
      return billingContactSentence(changes);
    case 'invitation.sent':
      return who ? `Invited ${who}${as(role(changes, 'after'))}` : null;
    case 'invitation.resent':
      return who
        ? `Resent ${who}’s invitation${inBrackets(role(changes, 'after'))}`
        : null;
    case 'invitation.revoked':
      return who
        ? `Revoked ${who}’s invitation${inBrackets(role(changes, 'before'))}`
        : null;
    case 'invitation.accepted': {
      const joinedAs = as(role(changes, 'after'));
      // The invitee accepts for themselves; nobody else is signed in when a
      // link is used. Naming them again would say the same person twice.
      if (entry.actor.id && entry.actor.id === entry.target.id) {
        return `Accepted their invitation and joined${joinedAs}`;
      }
      return who ? `${who} accepted an invitation${joinedAs}` : null;
    }
    case 'user.role_changed': {
      if (!who) return null;
      const change = find(changes, 'role');
      const both = change ? values(change) : null;
      return both ? `Changed ${who}’s role: ${both}` : `Changed ${who}’s role`;
    }
    case 'user.removed':
      return who ? `Removed ${who}` : null;
    case 'arc.details_updated':
      return arcDetailsSentence(changes);
    case 'arc.header_updated':
      return arcHeaderSentence(changes);
    case 'arc.section_created': {
      const section = arcName(changes, 'section');
      return section ? `Added the section ${section}` : null;
    }
    case 'arc.section_updated':
      return arcSectionSentence(changes);
    case 'arc.section_deleted': {
      const section = arcName(changes, 'section');
      return section ? `Deleted the section ${section}` : null;
    }
    case 'arc.sections_reordered':
      return 'Reordered the ARC’s sections';
    case 'arc.item_added': {
      const resource = arcName(changes, 'resource');
      const section = arcName(changes, 'section');
      return resource && section ? `Added ${resource} to ${section}` : null;
    }
    case 'arc.item_removed': {
      const resource = arcName(changes, 'resource');
      const section = arcName(changes, 'section');
      return resource && section ? `Removed ${resource} from ${section}` : null;
    }
    case 'arc.items_reordered':
      return arcItemsSentence(changes);
    case 'supporter.started':
    case 'supporter.ended':
    case 'supporter.notifications_changed':
    case 'member.notifications_changed':
      // An app user's own switch: the phrase says all there is to say.
      return null;
    case 'arc.published':
      return 'Published the ARC';
    case 'arc.unpublished':
      return 'Unpublished the ARC';
    case 'arc.draft_saved':
      return arcDraftSentence(changes);
    case 'link.updated':
    case 'link.published':
    case 'link.unpublished': {
      // The phrase, and which of the two links it was (P22, P36).
      const link = entry.target.label || targetTypeLabel(entry.target.type);
      return link ? `${actionPhrase(entry.action)} · ${link}` : null;
    }
    default:
      // A code the portal has no words for, shown as it came, with what it
      // was about when that is something other than the organization.
      return entry.target.label && entry.target.type !== 'organization'
        ? `${entry.action} · ${entry.target.label}`
        : entry.action;
  }
}

export function summarizeEntry(entry: AuditEntry, changes: AuditChange[]): string {
  return sentence(entry, changes) ?? actionPhrase(entry.action);
}
